/* eslint-disable max-lines */
import { Entity, moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { getGraphqlApiErrorCode, getGraphqlApiErrorReason } from '../..';
import { AcknowledgeStreamPolicyAction } from '../../domains/acknowledgeLiveEventPolicy/actions.types';
import { UpdateUserAdminRolesAction } from '../../domains/adminRoles/actions.types';
import {
  CampaignsListLoadMoreFulfilledAction,
  CampaignsListLoadMorePendingAction,
  CampaignsListLoadMoreRejectedAction,
} from '../../domains/allCampaigns/loadMoreAllCampaignsActions.types';
import { AllNetworkGroupsLoadMoreAction } from '../../domains/allNetworkGroups/actions.types';
import { AppViewPropertiesLoadAction } from '../../domains/appViewProperties/actions.types';
import { getAppViewPropertyRequestStatusId } from '../../domains/appViewProperties/getAppViewPropertyRequestStatusId';
import { AppViewPropertiesSetAction } from '../../domains/appViewProperties/setActions.types';
import { CampaignCoverImageDeleteAction } from '../../domains/campaignCoverImage/deleteActions.types';
import { CampaignCoverImageUploadAction } from '../../domains/campaignCoverImage/uploadActions.types';
import { CampaignDeleteAction } from '../../domains/campaignDeletion/actions.types';
import { CampaignEditAction } from '../../domains/campaignEdit/actions.types';
import { CampaignFollowAction } from '../../domains/campaignFollowing/followActions.types';
import { CampaignUnfollowAction } from '../../domains/campaignFollowing/unfollowActions.types';
import {
  CampaignGoalTrackerCreateAction,
  CampaignGoalTrackerEditAction,
} from '../../domains/campaignGoalTracker/actions.types';
import { CancelEventAction, CancelEventFulfilledAction } from '../../domains/cancelEvent/actions.types';
import { OfficeTopicLoadAuthorizedAction } from '../../domains/cortexTopics/actions.types';
import { CreateCalendarInviteForTeamsMeetingAction } from '../../domains/createCalendarInviteForTeamsMeeting/actions.types';
import { CreateGroupCalendarInviteForBroadcastAction } from '../../domains/createGroupCalendarInviteForBroadcast/actions.types';
import { CreateTeamsMeetingAction } from '../../domains/createTeamsMeeting/actions.types';
import { EditUserDelegateAction } from '../../domains/delegation/actions.types';
import { AddUserDelegateAction, RemoveUserDelegateAction } from '../../domains/delegationAddRemove/actions.types';
import { DeleteFileAction } from '../../domains/deleteFile/actions.types';
import { DeleteMessageAction } from '../../domains/deleteMessage/actions.types';
import { DeleteTeamsMeetingAction } from '../../domains/deleteTeamsMeeting/actions.types';
import { DeleteThreadAction } from '../../domains/deleteThread/actions.types';
import { EditEventAction, EditRestrictedPostingAction } from '../../domains/editEvent/actions.types';
import { EditTeamsMeetingAction } from '../../domains/editTeamsMeeting/actions.types';
import { EventLoadByBroadcastIdAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import { EditNetworkQuestionSettingsAction } from '../../domains/featureAccessSettings/editNetworkQuestionSettingsActions.types';
import { FeedCountsLoadGroupAction } from '../../domains/feedCounts/actions.types';
import { FeedLoadNewerThreadsAction, FeedLoadOlderThreadsAction } from '../../domains/feedLoad/actions.types';
import { FeedMarkAllGroupMessagesAsSeenAction } from '../../domains/feeds/actions.types';
import {
  GroupAddUserAction,
  GroupAddUserByEmailAction,
  GroupRemoveUserAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import { GroupAvatarUploadAction } from '../../domains/groupAvatarUploads/actions.types';
import { GroupCoverImageDeleteAction, GroupCoverImageUploadAction } from '../../domains/groupCoverImages/actions.types';
import { DeleteGroupActions } from '../../domains/groupDeletion/actions.types';
import {
  GroupDiscoverySettingsLoadAction,
  GroupHideForNetworkInDiscoveryAction,
  GroupHideForViewerInDiscoveryAction,
  GroupUnhideForNetworkInDiscoveryAction,
  GroupUnhideForViewerInDiscoveryAction,
} from '../../domains/groupDiscoverySettings/actions.types';
import { GroupEventsSidebarLoadAction } from '../../domains/groupEventsSidebarLoad/actions.types';
import { GroupMakeAdminAction, GroupRevokeAdminAction } from '../../domains/groupMakeRevokeAdmin/actions.types';
import {
  GroupMemberPanelsDismissActionErrorsAction,
  GroupMemberPanelsLoadMoreAction,
} from '../../domains/groupMemberPanels/actions.types';
import { GroupMembersBulkUploadAction } from '../../domains/groupMembersBulkUpload/actions.types';
import { ResolveGroupMembershipAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import { GroupEditActions } from '../../domains/groups/actions.types';
import { InboxUnreadCountLoadAction } from '../../domains/inboxUnreadCountLoad/actions.types';
import {
  DesignateLeadersFromOfficeGroupAsyncAction,
  UpdateHasNetworkWideAudienceAction,
  UserConnectedLeadersListLoadMoreAction,
  UserDirectAudienceLoadMoreAction,
  UserFollowedLeadersListLoadMoreAction,
  UserNetworkLeadersListLoadMoreAction,
} from '../../domains/leadersManagement/actions.types';
import { AddToDirectAudienceAction } from '../../domains/leadersManagement/addToDirectAudienceActions.types';
import { DesignateLeaderAction } from '../../domains/leadersManagement/designateLeaderActions.types';
import {
  RemoveFromDirectAudienceAction,
  RemoveFromDirectAudienceFulfilledAction,
} from '../../domains/leadersManagement/removeFromDirectAudienceActions.types';
import { RevokeLeaderAction } from '../../domains/leadersManagement/revokeLeaderActions.types';
import { LiveEventDownloadIcsFileAction } from '../../domains/liveEventDownloadIcsFile/actions.types';
import { LiveEventsLoadByNetworkAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import { MessageUpvotesLoadMoreAction } from '../../domains/messageUpvoteLoad/paginateActions.types';
import {
  DismissModeratedThreadAction,
  PublishModeratedThreadAction,
  RestoreModeratedThreadAction,
} from '../../domains/messageWithModerationState/actions.types';
import { MyGroupsLoadMoreAction } from '../../domains/myGroups/actions.types';
import { NestedThreadListLoadAction } from '../../domains/nestedThreadList/actions.types';
import { NetworkQuestionInfoPanelEditAction } from '../../domains/networkQuestionInfoPanel/editActions.types';
import { MarkAllNotificationsAsSeenAction } from '../../domains/notificationFeeds/actions.types';
import {
  NotificationLoadMoreAction,
  NotificationUnseenCountLoadAction,
} from '../../domains/notificationPaginations/actions.types';
import { GroupMarkAsOfficialAction, GroupUnmarkAsOfficialAction } from '../../domains/officialGroup/actions.types';
import {
  EditPinnedLinkTitleFulfilledAction,
  EditPinnedLinkTitlePendingAction,
  EditPinnedLinkTitleRejectedAction,
  PinCampaignObjectFulfilledAction,
  PinCampaignObjectPendingAction,
  PinCampaignObjectRejectedAction,
  PinObjectFulfilledAction,
  PinObjectPendingAction,
  PinObjectRejectedAction,
} from '../../domains/pinnedResources/actions.types';
import {
  ThreadPinToCampaignFeedAction,
  ThreadPinToEventFeedAction,
  ThreadPinToGroupFeedAction,
  ThreadPinToTeamsMeetingFeedAction,
  ThreadPinToUserStorylineFeedAction,
  ThreadUnpinFromCampaignFeedAction,
  ThreadUnpinFromEventFeedAction,
  ThreadUnpinFromGroupFeedAction,
  ThreadUnpinFromTeamsMeetingFeedAction,
  ThreadUnpinFromUserStorylineFeedAction,
} from '../../domains/pinnedThreads/actions.types';
import { PollLoadAction } from '../../domains/polls/actions.types';
import { PromoteThreadAction, UnpromoteThreadAction } from '../../domains/promoteThread/types';
import {
  PublishDirectMessageAction,
  PublishEditMessageAction,
  PublishEventMessageAction,
  PublishGroupMessageAction,
  PublishNetworkQuestionMessageAction,
  PublishPollMessageAction,
  PublishPraiseMessageAction,
  PublishReplyMessageAction,
  PublishTeamsMeetingMessageAction,
  PublishUserMessageAction,
  PublishUserMomentMessageAction,
  PublishUserPollMessageAction,
  PublishUserPraiseMessageAction,
  PublishersRemoveAction,
} from '../../domains/publishers/actions.types';
import { ReportMessageAction } from '../../domains/reportMessage/reportActions.types';
import { getId } from '../../domains/requestStatuses/getId';
import { ResetAction } from '../../domains/requestStatuses/types';
import { ResolveWebLinkAction } from '../../domains/resolveWebLink/actions.types';
import { SearchLoadMoreAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastAction,
  SecondLevelRepliesLoadNextAction,
  SecondLevelRepliesLoadPreviousAction,
} from '../../domains/secondLevelReplies/actions.types';
import { UpdateSessionReactionAccentColorAction } from '../../domains/sessionReactionAccentColor/actions.types';
import { SharePointVideoLinkLoadAction } from '../../domains/sharePointVideoLinks/actions.types';
import { UpdateStorylineDefaultNotificationDeliverySettingsAction } from '../../domains/storylineAdminSettings/updateDefaultNotificationDeliverySettingsActions.types';
import { SuggestedTopicsLoadMoreAction } from '../../domains/suggestedTopics/loadMoreActions.types';
import { TeamsMeetingLoadAction } from '../../domains/teamsMeetingLoad/actions.types';
import { ThreadBookmarkAction, ThreadUnbookmarkAction } from '../../domains/threadBookmark/types';
import { ThreadCloseAction, ThreadReopenAction } from '../../domains/threadClose/actions.types';
import { ThreadListLoadAction } from '../../domains/threadListLoad/actions.types';
import { ThreadLoadNewerRepliesAction } from '../../domains/threadLoad/actions.types';
import { ThreadMarkAsQuestionAction, ThreadUnmarkAsQuestionAction } from '../../domains/threadMarkAsQuestion/types';
import { ThreadMoveAction } from '../../domains/threadMove/actions.types';
import { RemoveThreadParticipantsAction } from '../../domains/threadRemoveParticipants/actions.types';
import { ThreadSharesLoadAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialAction,
  TopLevelRepliesLoadLastAction,
  TopLevelRepliesLoadNextAction,
  TopLevelRepliesLoadPreviousAction,
} from '../../domains/topLevelReplies/actions.types';
import { TopicSidebarLoadAction } from '../../domains/topicSidebars/actions.types';
import {
  AddThreadTopicActions,
  RemoveCuratedTopicAction,
  RemoveThreadTopicActions,
  TopicCurateAction,
  TopicUncurateAction,
} from '../../domains/topics/actions.types';
import { TopicEditAction } from '../../domains/topics/editActions.types';
import { TopicsCuratedLoadAction, TopicsCuratedLoadMoreAction } from '../../domains/topicsCurated/actions.types';
import { TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/loadMoreActions.types';
import { TrendingTopicsLoadMoreAction } from '../../domains/trendingTopicsLoad/loadMoreActions.types';
import { UnseenThreadsLoadAction } from '../../domains/unseenThreadsLoad/actions.types';
import { EditUserAchievementAdminSettingsAction } from '../../domains/userAchievementsSettings/editUserAchievementAdminSettingsActions.types';
import { EditUserAchievementUserSettingsAction } from '../../domains/userAchievementsSettings/editUserAchievementUserSettingsActions.types';
import { UserCoverImageDeleteAction, UserCoverImageUploadAction } from '../../domains/userCoverImages/actions.types';
import { AddFavoriteCommunityRejectedAction } from '../../domains/viewerCommunityRelationships/actions.types';

import {
  RequestStatusType,
  RequestStatuses,
  acknowledgeStreamEventPolicyRequestId,
  addFavoriteGroupRequestId,
  addUserDelegateRequestId,
  editNetworkQuestionInfoPanelRequestId,
  editNetworkQuestionsSettingsRequestId,
  editUserDelegateRequestId,
  inboxUnreadCountRequestId,
  liveEventsLoadByNetworkRequestId,
  notificationUnseenCountRequestId,
  removeUserDelegateRequestId,
  suggestedTopicsLoadMoreRequestId,
  threadListLoadRequestId,
  updateReactionAccentColorRequestId,
  updateStorylineDefaultNotificationDeliverySettingsRequestId,
} from './types';

type GroupPinnedResourceActions =
  | EditPinnedLinkTitleFulfilledAction
  | EditPinnedLinkTitlePendingAction
  | GroupSidebarLoadFulfilledAction
  | PinObjectFulfilledAction
  | PinObjectRejectedAction;

type CampaignPinnedResourceActions =
  | PinCampaignObjectPendingAction
  | PinCampaignObjectFulfilledAction
  | PinCampaignObjectRejectedAction;

type CampaignsListLoadActions =
  | CampaignsListLoadMoreFulfilledAction
  | CampaignsListLoadMorePendingAction
  | CampaignsListLoadMoreRejectedAction;

type PublishMessageRequestAction =
  | PublishGroupMessageAction
  | PublishEventMessageAction
  | PublishUserMessageAction
  | PublishUserMomentMessageAction
  | PublishUserPollMessageAction
  | PublishUserPraiseMessageAction
  | PublishDirectMessageAction
  | PublishEditMessageAction
  | PublishReplyMessageAction
  | PublishPollMessageAction
  | PublishPraiseMessageAction
  | PublishTeamsMeetingMessageAction
  | PublishNetworkQuestionMessageAction;

type PinnedResourceModalActions = EditPinnedLinkTitleRejectedAction | PinObjectPendingAction;

export type RequestStatusAction =
  | AcknowledgeStreamPolicyAction
  | AddFavoriteCommunityRejectedAction
  | AddThreadTopicActions
  | AddToDirectAudienceAction
  | AddUserDelegateAction
  | AllNetworkGroupsLoadMoreAction
  | AppViewPropertiesLoadAction
  | AppViewPropertiesSetAction
  | CampaignPinnedResourceActions
  | CampaignsListLoadActions
  | CampaignFollowAction
  | CampaignUnfollowAction
  | CampaignGoalTrackerCreateAction
  | CampaignGoalTrackerEditAction
  | CancelEventAction
  | CreateCalendarInviteForTeamsMeetingAction
  | CreateGroupCalendarInviteForBroadcastAction
  | CreateTeamsMeetingAction
  | DeleteTeamsMeetingAction
  | DeleteFileAction
  | DeleteGroupActions
  | DeleteMessageAction
  | DeleteThreadAction
  | DesignateLeaderAction
  | DismissModeratedThreadAction
  | EditEventAction
  | EditNetworkQuestionSettingsAction
  | EditRestrictedPostingAction
  | EditTeamsMeetingAction
  | EditUserAchievementAdminSettingsAction
  | EditUserAchievementUserSettingsAction
  | EditUserDelegateAction
  | EventLoadByBroadcastIdAction
  | FeedCountsLoadGroupAction
  | FeedLoadNewerThreadsAction
  | FeedLoadOlderThreadsAction
  | FeedMarkAllGroupMessagesAsSeenAction
  | GroupAddUserAction
  | GroupAddUserByEmailAction
  | GroupAvatarUploadAction
  | GroupCoverImageDeleteAction
  | GroupCoverImageUploadAction
  | GroupDiscoverySettingsLoadAction
  | GroupEditActions
  | GroupEventsSidebarLoadAction
  | GroupHideForNetworkInDiscoveryAction
  | GroupHideForViewerInDiscoveryAction
  | GroupMakeAdminAction
  | GroupMarkAsOfficialAction
  | GroupMemberPanelsDismissActionErrorsAction
  | GroupMemberPanelsLoadMoreAction
  | GroupMembersBulkUploadAction
  | GroupPinnedResourceActions
  | GroupRemoveUserAction
  | GroupRevokeAdminAction
  | GroupUnhideForNetworkInDiscoveryAction
  | GroupUnhideForViewerInDiscoveryAction
  | GroupUnmarkAsOfficialAction
  | InboxUnreadCountLoadAction
  | LiveEventDownloadIcsFileAction
  | LiveEventsLoadByNetworkAction
  | MarkAllNotificationsAsSeenAction
  | MessageUpvotesLoadMoreAction
  | MyGroupsLoadMoreAction
  | NestedThreadListLoadAction
  | NetworkQuestionInfoPanelEditAction
  | NotificationLoadMoreAction
  | NotificationUnseenCountLoadAction
  | OfficeTopicLoadAuthorizedAction
  | PinnedResourceModalActions
  | PollLoadAction
  | PromoteThreadAction
  | PublishersRemoveAction
  | PublishMessageRequestAction
  | PublishModeratedThreadAction
  | RemoveFromDirectAudienceAction
  | RemoveThreadParticipantsAction
  | RemoveThreadTopicActions
  | RemoveUserDelegateAction
  | RevokeLeaderAction
  | ReportMessageAction
  | ResetAction
  | ResolveGroupMembershipAction
  | ResolveWebLinkAction
  | RestoreModeratedThreadAction
  | SearchLoadMoreAction
  | SecondLevelRepliesLoadLastAction
  | SecondLevelRepliesLoadNextAction
  | SecondLevelRepliesLoadPreviousAction
  | SharePointVideoLinkLoadAction
  | SuggestedTopicsLoadMoreAction
  | TeamsMeetingLoadAction
  | ThreadBookmarkAction
  | ThreadCloseAction
  | ThreadListLoadAction
  | ThreadLoadNewerRepliesAction
  | ThreadMarkAsQuestionAction
  | ThreadMoveAction
  | ThreadPinToCampaignFeedAction
  | ThreadPinToEventFeedAction
  | ThreadPinToGroupFeedAction
  | ThreadPinToTeamsMeetingFeedAction
  | ThreadPinToUserStorylineFeedAction
  | ThreadReopenAction
  | ThreadSharesLoadAction
  | ThreadUnbookmarkAction
  | ThreadUnmarkAsQuestionAction
  | ThreadUnpinFromCampaignFeedAction
  | ThreadUnpinFromEventFeedAction
  | ThreadUnpinFromGroupFeedAction
  | ThreadUnpinFromTeamsMeetingFeedAction
  | ThreadUnpinFromUserStorylineFeedAction
  | TopicEditAction
  | TopicCurateAction
  | TopicsCuratedLoadAction
  | TopicsCuratedLoadMoreAction
  | RemoveCuratedTopicAction
  | TopicSidebarLoadAction
  | TopLevelRepliesLoadInitialAction
  | TopicUncurateAction
  | TopLevelRepliesLoadLastAction
  | TopLevelRepliesLoadNextAction
  | TopLevelRepliesLoadPreviousAction
  | TopicsFollowedLoadMoreAction
  | TrendingTopicsLoadMoreAction
  | UnpromoteThreadAction
  | UnseenThreadsLoadAction
  | UpdateHasNetworkWideAudienceAction
  | UpdateSessionReactionAccentColorAction
  | UserCoverImageDeleteAction
  | UserCoverImageUploadAction
  | CampaignCoverImageDeleteAction
  | CampaignCoverImageUploadAction
  | CampaignEditAction
  | CampaignDeleteAction
  | UserNetworkLeadersListLoadMoreAction
  | UserDirectAudienceLoadMoreAction
  | UserConnectedLeadersListLoadMoreAction
  | UserFollowedLeadersListLoadMoreAction
  | UpdateUserAdminRolesAction
  | UpdateStorylineDefaultNotificationDeliverySettingsAction
  | DesignateLeadersFromOfficeGroupAsyncAction;

const initialState: RequestStatuses = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const moveRequestToPending = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id']
): RequestStatuses => moveToPending(state, [getId(type, entityId)]);

const moveRequestToFulfilled = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id']
): RequestStatuses => {
  const id = getId(type, entityId);

  return moveToFulfilled(state, { [id]: { id, fulfilledTime: new Date().getTime() } });
};

const moveRequestToRejected = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id'],
  error: unknown
): RequestStatuses => moveToRejected(state, [getId(type, entityId)], error);

type RemoveRequest = (state: RequestStatuses, type: RequestStatusType, entityId: Entity['id']) => RequestStatuses;
const removeRequest: RemoveRequest = (state, type, entityId) => remove(state, [getId(type, entityId)]);

const groupMemberPanelRelatedActions = [
  RequestStatusType.GroupAddUserRequest,
  RequestStatusType.GroupAddUserByEmailRequest,
  RequestStatusType.GroupRemoveUserRequest,
  RequestStatusType.GroupMakeAdminRequest,
  RequestStatusType.GroupRevokeAdminRequest,
  RequestStatusType.GroupRevokeAdminRequest,
  RequestStatusType.GroupResolveMembershipRequest,
  RequestStatusType.GroupMembersBulkUploadRequest,
];
const moveRequestToPendingAndRemoveRelatedRequestTypes = (
  state: RequestStatuses,
  type: RequestStatusType,
  relatedTypes: RequestStatusType[],
  entityId: Entity['id']
): RequestStatuses => {
  const stateWithoutRelatedActions = removeRelatedRequestTypes(state, relatedTypes, entityId);

  return moveToPending(stateWithoutRelatedActions, [getId(type, entityId)]);
};

const removeRelatedRequestTypes = (
  state: RequestStatuses,
  relatedTypes: RequestStatusType[],
  entityId: Entity['id']
): RequestStatuses =>
  relatedTypes.reduce((newState, relatedType) => removeRequest(newState, relatedType, entityId), state);

interface MoveRequestToPendingAndRemoveAssociatedRequestArgs {
  readonly pendingType: RequestStatusType;
  readonly removeType: RequestStatusType;
  readonly entityId: Entity['id'];
}

const moveRequestToPendingAndRemoveAssociatedRequest = (
  state: RequestStatuses,
  { pendingType, removeType, entityId }: MoveRequestToPendingAndRemoveAssociatedRequestArgs
) => {
  const stateWithoutAssociatedRequest = removeRequest(state, removeType, entityId);

  return moveRequestToPending(stateWithoutAssociatedRequest, pendingType, entityId);
};

const stateWithCancelledEvent = (state: RequestStatuses, { payload: { broadcastId } }: CancelEventFulfilledAction) => {
  const stateWithFulfilledCancelledEventRequest = moveRequestToFulfilled(
    state,
    RequestStatusType.CancelEventRequest,
    broadcastId
  );

  return removeRequest(
    stateWithFulfilledCancelledEventRequest,
    RequestStatusType.EventLoadByBroadcastIdRequest,
    broadcastId
  );
};

const stateWithRemovedFromDirectAudience = (
  state: RequestStatuses,
  { payload: { userId } }: RemoveFromDirectAudienceFulfilledAction
) => {
  const stateWithFulfilledRemovedFromDirectAudienceRequest = moveRequestToFulfilled(
    state,
    RequestStatusType.RemoveFromDirectAudienceRequest,
    userId
  );

  return removeRequest(
    stateWithFulfilledRemovedFromDirectAudienceRequest,
    RequestStatusType.RemoveFromDirectAudienceRequest,
    userId
  );
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<RequestStatuses, RequestStatusAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'REQUEST_STATUSES.RESET':
      return removeRequest(state, action.payload.type, action.payload.entityId);
    case 'ADD_TO_DIRECT_AUDIENCE.PENDING':
      return moveRequestToPending(state, RequestStatusType.AddToDirectAudienceRequest, action.payload.userId);
    case 'ADD_TO_DIRECT_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.AddToDirectAudienceRequest, action.payload.userId);
    case 'ADD_TO_DIRECT_AUDIENCE.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AddToDirectAudienceRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.AllNetworkGroupsLoadMoreRequest, action.payload.sortBy);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_FULFILLED':
      return removeRequest(state, RequestStatusType.AllNetworkGroupsLoadMoreRequest, action.payload.sortBy);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AllNetworkGroupsLoadMoreRequest,
        action.payload.sortBy,
        action.payload.error
      );

    case 'APP_VIEW_PROPERTIES.LOAD_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.AppViewPropertiesRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys)
      );
    case 'APP_VIEW_PROPERTIES.LOAD_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.AppViewPropertiesRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys)
      );
    case 'APP_VIEW_PROPERTIES.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AppViewPropertiesRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys),
        action.payload.error
      );

    case 'APP_VIEW_PROPERTIES.SET_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.AppViewPropertiesSetRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey])
      );
    case 'APP_VIEW_PROPERTIES.SET_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.AppViewPropertiesSetRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey])
      );
    case 'APP_VIEW_PROPERTIES.SET_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AppViewPropertiesSetRequest,
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey]),
        action.payload.error
      );

    case 'CREATE_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, RequestStatusType.CreateTeamsMeetingRequest, action.payload.officeMeetingId);
    case 'CREATE_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CreateTeamsMeetingRequest, action.payload.officeMeetingId);
    case 'CREATE_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CreateTeamsMeetingRequest,
        action.payload.officeMeetingId,
        action.payload.error
      );

    case 'DELETE_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, RequestStatusType.DeleteTeamsMeetingRequest, action.payload.id);
    case 'DELETE_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteTeamsMeetingRequest, action.payload.id);
    case 'DELETE_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DeleteTeamsMeetingRequest,
        action.payload.id,
        action.payload.error
      );

    case 'TEAMS_MEETING.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.TeamsMeetingLoadRequest, action.payload);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TeamsMeetingLoadRequest, action.payload.teamsMeetingId);
    case 'TEAMS_MEETING.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TeamsMeetingLoadRequest,
        action.payload.teamsMeetingId,
        action.payload.error
      );

    case 'EDIT_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, RequestStatusType.EditTeamsMeetingRequest, action.payload.teamsMeetingId);
    case 'EDIT_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.EditTeamsMeetingRequest, action.payload.teamsMeetingId);
    case 'EDIT_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditTeamsMeetingRequest,
        action.payload.teamsMeetingId,
        action.payload.error
      );

    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.EditUserAchievementAdminSettingsRequest,
        removeType: RequestStatusType.EditUserAchievementAdminSettingsRequest,
        entityId: action.editUserAchievementAdminSettingsRequestStatusKey,
      });
    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.EditUserAchievementAdminSettingsRequest,
        action.editUserAchievementAdminSettingsRequestStatusKey
      );
    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditUserAchievementAdminSettingsRequest,
        action.editUserAchievementAdminSettingsRequestStatusKey,
        action.payload.error
      );
    case 'DISMISS_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'DISMISS_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'DISMISS_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ModerateThreadRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'DESIGNATE_LEADER.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.DesignateLeaderRequest,
        removeType: RequestStatusType.RevokeLeaderRequest,
        entityId: action.payload.userId,
      });
    case 'DESIGNATE_LEADER.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DesignateLeaderRequest, action.payload.userId);
    case 'DESIGNATE_LEADER.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DesignateLeaderRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.EditUserAchievementUserSettingsRequest,
        removeType: RequestStatusType.EditUserAchievementUserSettingsRequest,
        entityId: action.payload.userId,
      });
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.EditUserAchievementUserSettingsRequest,
        action.payload.userId
      );
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditUserAchievementUserSettingsRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'FEED.LOAD_NEWER_THREADS_PENDING':
      return moveRequestToPending(state, RequestStatusType.FeedLoadNewerThreadsRequest, action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.FeedLoadNewerThreadsRequest, action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.FeedLoadNewerThreadsRequest,
        action.payload.id,
        action.payload.error
      );
    case 'FEED.LOAD_OLDER_THREADS_PENDING':
      return moveRequestToPending(state, RequestStatusType.FeedLoadOlderThreadsRequest, action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.FeedLoadOlderThreadsRequest, action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.FeedLoadOlderThreadsRequest,
        action.payload.id,
        action.payload.error
      );

    case 'FILE.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.DeleteFileRequest, action.payload.id);
    case 'FILE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteFileRequest, action.payload.id);
    case 'FILE.IMAGE_DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteFileRequest, action.payload.id);
    case 'FILE.VIDEO_DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteFileRequest, action.payload.id);
    case 'FILE.DELETE_REJECTED':
      return moveRequestToRejected(state, RequestStatusType.DeleteFileRequest, action.payload.id, action.payload.error);

    case 'GROUP.ADD_USER_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupAddUserRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.ADD_USER_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupAddUserRequest, action.payload.groupId);
    case 'GROUP.ADD_USER_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupAddUserRequest,
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.ADD_USER_BY_EMAIL_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupAddUserByEmailRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupAddUserByEmailRequest, action.payload.groupId);
    case 'GROUP.ADD_USER_BY_EMAIL_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupAddUserByEmailRequest,
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.REMOVE_USER_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupRemoveUserRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.REMOVE_USER_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupRemoveUserRequest, action.payload.groupId);
    case 'GROUP.REMOVE_USER_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupRemoveUserRequest,
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.MAKE_ADMIN_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupMakeAdminRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.MAKE_ADMIN_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupMakeAdminRequest, action.payload.groupId);
    case 'GROUP.MAKE_ADMIN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupMakeAdminRequest,
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.REVOKE_ADMIN_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupRevokeAdminRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.REVOKE_ADMIN_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupRevokeAdminRequest, action.payload.groupId);
    case 'GROUP.REVOKE_ADMIN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupRevokeAdminRequest,
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP_MEMBER_PANELS.DISMISS_ERRORS':
      return removeRelatedRequestTypes(state, groupMemberPanelRelatedActions, action.groupId);

    case 'GROUP_MEMBER_PANELS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupMemberPanelPaginationsRequest, action.payload.groupId);
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupMemberPanelPaginationsRequest,
        action.payload.groupId
      );
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupMemberPanelPaginationsRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageDeleteRequest, action.payload.groupId);
    case 'GROUP_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageDeleteRequest, action.payload.groupId);
    case 'GROUP_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageDeleteRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageUploadRequest, action.payload.groupId);
    case 'GROUP_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageUploadRequest, action.payload.groupId);
    case 'GROUP_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageUploadRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.EDIT_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupEditRequest, action.payload.groupId);
    case 'GROUP.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupEditRequest, action.payload.groupId);
    case 'GROUP.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupEditRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupDeletionRequest, action.payload.groupId);
    case 'GROUP.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupDeletionRequest, action.payload.groupId);
    case 'GROUP.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupDeletionRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_EVENTS_SIDEBAR.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupEventsSidebarLoadRequest, action.payload.groupId);
    case 'GROUP_EVENTS_SIDEBAR.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupEventsSidebarLoadRequest, action.payload.groupId);
    case 'GROUP_EVENTS_SIDEBAR.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupEventsSidebarLoadRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.GroupHideForNetworkInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupHideForNetworkInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupHideForNetworkInDiscoveryRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.MARK_AS_OFFICIAL_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.GroupMarkAsOfficialRequest,
        removeType: RequestStatusType.GroupUnmarkAsOfficialRequest,
        entityId: action.payload.groupId,
      });
    case 'GROUP.MARK_AS_OFFICIAL_FULFILLED':
      return moveRequestToPending(state, RequestStatusType.GroupMarkAsOfficialRequest, action.payload.groupId);
    case 'GROUP.MARK_AS_OFFICIAL_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupMarkAsOfficialRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_MEMBERS.BULK_UPLOAD_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupMembersBulkUploadRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP_MEMBERS.BULK_UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupMembersBulkUploadRequest, action.payload.groupId);
    case 'GROUP_MEMBERS.BULK_UPLOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupMembersBulkUploadRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.UNMARK_AS_OFFICIAL_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.GroupUnmarkAsOfficialRequest,
        removeType: RequestStatusType.GroupMarkAsOfficialRequest,
        entityId: action.payload.groupId,
      });
    case 'GROUP.UNMARK_AS_OFFICIAL_FULFILLED':
      return moveRequestToPending(state, RequestStatusType.GroupUnmarkAsOfficialRequest, action.payload.groupId);
    case 'GROUP.UNMARK_AS_OFFICIAL_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupUnmarkAsOfficialRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.GroupUnhideForNetworkInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupUnhideForNetworkInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupUnhideForNetworkInDiscoveryRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.GroupHideForViewerInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupHideForViewerInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupHideForViewerInDiscoveryRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.GroupUnhideForViewerInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupUnhideForViewerInDiscoveryRequest,
        action.payload.groupId
      );
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupUnhideForViewerInDiscoveryRequest,
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_DISCOVERY_SETTINGS.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupDiscoverySettingsLoadRequest, action.payload.groupId);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupDiscoverySettingsLoadRequest,
        action.payload.groupId,
        action.payload.error
      );
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupDiscoverySettingsLoadRequest, action.payload.groupId);

    case 'GROUP_AVATAR_UPLOAD.PENDING':
      return removeRequest(state, RequestStatusType.CoverImageUploadRequest, action.payload.groupId);

    case 'MESSAGE.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.DeleteMessageRequest, action.payload.messageId);
    case 'MESSAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteMessageRequest, action.payload.messageId);
    case 'MESSAGE.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DeleteMessageRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'MESSAGE.REPORT_PENDING':
      return moveRequestToPending(state, RequestStatusType.ReportMessageRequest, action.payload.messageId);
    case 'MESSAGE.REPORT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ReportMessageRequest, action.payload.messageId);
    case 'MESSAGE.REPORT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ReportMessageRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'MESSAGE_UPVOTES.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.MessageUpvotesLoadMoreRequest, action.payload.messageId);
    case 'MESSAGE_UPVOTES.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.MessageUpvotesLoadMoreRequest, action.payload.messageId);
    case 'MESSAGE_UPVOTES.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.MessageUpvotesLoadMoreRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'NETWORK_QUESTION_INFO_PANEL.EDIT_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.EditNetworkQuestionInfoPanel,
        editNetworkQuestionInfoPanelRequestId
      );
    case 'NETWORK_QUESTION_INFO_PANEL.EDIT_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.EditNetworkQuestionInfoPanel,
        editNetworkQuestionInfoPanelRequestId
      );
    case 'NETWORK_QUESTION_INFO_PANEL.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditNetworkQuestionInfoPanel,
        editNetworkQuestionInfoPanelRequestId,
        action.payload.error
      );

    case 'NETWORK_QUESTION_SETTINGS.EDIT_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.EditNetworkQuestionSettings,
        editNetworkQuestionsSettingsRequestId
      );
    case 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.EditNetworkQuestionSettings,
        editNetworkQuestionsSettingsRequestId
      );
    case 'NETWORK_QUESTION_SETTINGS.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditNetworkQuestionSettings,
        editNetworkQuestionsSettingsRequestId,
        action.payload.error
      );

    case 'OFFICE_TOPIC.LOAD_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.OfficeTopicLoadAuthorizedRequest,
        action.payload.publisherId
      );
    case 'OFFICE_TOPIC.LOAD_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.OfficeTopicLoadAuthorizedRequest,
        action.payload.publisherId
      );
    case 'OFFICE_TOPIC.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.OfficeTopicLoadAuthorizedRequest,
        action.payload.publisherId,
        action.payload.error
      );

    case 'POLL.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.PollLoadRequest, action.payload.id);
    case 'POLL.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PollLoadRequest, action.payload.id);
    case 'POLL.LOAD_REJECTED':
      return moveRequestToRejected(state, RequestStatusType.PollLoadRequest, action.payload.id, action.payload.error);
    case 'PUBLISH_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'PUBLISH_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'PUBLISH_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ModerateThreadRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PublishMessageRequest, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PublishMessageRequest,
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.REMOVE':
      return removeRequest(state, RequestStatusType.PublishMessageRequest, action.payload.id);

    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.MarkAllNotificationsAsSeenRequest, action.payload.id);
    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.MarkAllNotificationsAsSeenRequest, action.payload.id);
    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.MarkAllNotificationsAsSeenRequest,
        action.payload.id,
        action.payload.error
      );

    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_PENDING':
      return moveRequestToPending(state, RequestStatusType.MarkAllGroupThreadsAsSeenRequest, action.payload.feedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.MarkAllGroupThreadsAsSeenRequest, action.payload.feedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.MarkAllGroupThreadsAsSeenRequest,
        action.payload.feedId,
        action.payload.error
      );

    case 'NOTIFICATION.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.NotificationLoadMoreRequest, action.payload.requestId);
    case 'NOTIFICATION.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.NotificationLoadMoreRequest, action.payload.requestId);
    case 'NOTIFICATION.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.NotificationLoadMoreRequest,
        action.payload.requestId,
        action.payload.error
      );

    case 'NOTIFICATION_UNSEEN_COUNT.LOAD_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.NotificationUnseenCountLoadRequest,
        notificationUnseenCountRequestId
      );
    case 'NOTIFICATION_UNSEEN_COUNT.LOAD_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.NotificationUnseenCountLoadRequest,
        notificationUnseenCountRequestId
      );
    case 'NOTIFICATION_UNSEEN_COUNT.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.NotificationUnseenCountLoadRequest,
        notificationUnseenCountRequestId,
        action.payload.error
      );
    case 'REMOVE_FROM_DIRECT_AUDIENCE.PENDING':
      return moveRequestToPending(state, RequestStatusType.RemoveFromDirectAudienceRequest, action.payload.userId);
    case 'REMOVE_FROM_DIRECT_AUDIENCE.FULFILLED':
      return stateWithRemovedFromDirectAudience(state, action);
    case 'REMOVE_FROM_DIRECT_AUDIENCE.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.RemoveFromDirectAudienceRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'RESTORE_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'RESTORE_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ModerateThreadRequest, action.payload.threadId);
    case 'RESTORE_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ModerateThreadRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'REVOKE_LEADER.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.RevokeLeaderRequest,
        removeType: RequestStatusType.DesignateLeaderRequest,
        entityId: action.payload.userId,
      });
    case 'REVOKE_LEADER.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.RevokeLeaderRequest, action.payload.userId);
    case 'REVOKE_LEADER.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.RevokeLeaderRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'THREAD.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.DeleteThreadRequest, action.payload.threadId);
    case 'THREAD.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DeleteThreadRequest, action.payload.threadId);
    case 'THREAD.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DeleteThreadRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'THREAD.PIN_TO_CAMPAIGN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadPinToCampaignFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromCampaignFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_CAMPAIGN_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadPinToCampaignFeedRequest, action.payload.threadId);
    case 'THREAD.PIN_TO_CAMPAIGN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadPinToCampaignFeedRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'THREAD.PIN_TO_EVENT_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadPinToEventFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromEventFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadPinToEventFeedRequest, action.payload.threadId);
    case 'THREAD.PIN_TO_EVENT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadPinToEventFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.PIN_TO_USER_STORYLINE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadPinToUserStorylineFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromUserStorylineFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_USER_STORYLINE_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ThreadPinToUserStorylineFeedRequest,
        action.payload.threadId
      );
    case 'THREAD.PIN_TO_USER_STORYLINE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadPinToUserStorylineFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.PIN_TO_GROUP_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadPinToGroupFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromGroupFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadPinToGroupFeedRequest, action.payload.threadId);
    case 'THREAD.PIN_TO_GROUP_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadPinToGroupFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.PIN_TO_TEAMS_MEETING_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadPinToTeamsMeetingFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromTeamsMeetingFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ThreadPinToTeamsMeetingFeedRequest,
        action.payload.threadId
      );
    case 'THREAD.PIN_TO_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadPinToTeamsMeetingFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_CAMPAIGN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnpinFromCampaignFeedRequest,
        removeType: RequestStatusType.ThreadPinToCampaignFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_CAMPAIGN_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ThreadUnpinFromCampaignFeedRequest,
        action.payload.threadId
      );
    case 'THREAD.UNPIN_FROM_CAMPAIGN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnpinFromCampaignFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_EVENT_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnpinFromEventFeedRequest,
        removeType: RequestStatusType.ThreadPinToEventFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadUnpinFromEventFeedRequest, action.payload.threadId);
    case 'THREAD.UNPIN_FROM_EVENT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnpinFromEventFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_USER_STORYLINE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnpinFromUserStorylineFeedRequest,
        removeType: RequestStatusType.ThreadUnpinFromUserStorylineFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_USER_STORYLINE_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ThreadUnpinFromUserStorylineFeedRequest,
        action.payload.threadId
      );
    case 'THREAD.UNPIN_FROM_USER_STORYLINE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnpinFromUserStorylineFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_GROUP_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnpinFromGroupFeedRequest,
        removeType: RequestStatusType.ThreadPinToGroupFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadUnpinFromGroupFeedRequest, action.payload.threadId);
    case 'THREAD.UNPIN_FROM_GROUP_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnpinFromGroupFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnpinFromTeamsMeetingFeedRequest,
        removeType: RequestStatusType.ThreadPinToTeamsMeetingFeedRequest,
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ThreadUnpinFromTeamsMeetingFeedRequest,
        action.payload.threadId
      );
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnpinFromTeamsMeetingFeedRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.CLOSE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadCloseRequest,
        removeType: RequestStatusType.ThreadReopenRequest,
        entityId: action.payload.id,
      });
    case 'THREAD.CLOSE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadCloseRequest, action.payload.id);
    case 'THREAD.CLOSE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadCloseRequest,
        action.payload.id,
        action.payload.error
      );

    case 'THREAD.REOPEN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadReopenRequest,
        removeType: RequestStatusType.ThreadCloseRequest,
        entityId: action.payload.id,
      });
    case 'THREAD.REOPEN_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadReopenRequest, action.payload.id);
    case 'THREAD.REOPEN_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadReopenRequest,
        action.payload.id,
        action.payload.error
      );

    case 'THREAD.BOOKMARK_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadBookmarkRequest, action.payload.threadId);
    case 'THREAD.BOOKMARK_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadBookmarkRequest, action.payload.threadId);
    case 'THREAD.BOOKMARK_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadBookmarkRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNBOOKMARK_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadUnbookmarkRequest, action.payload.threadId);
    case 'THREAD.UNBOOKMARK_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadUnbookmarkRequest, action.payload.threadId);
    case 'THREAD.UNBOOKMARK_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnbookmarkRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.MOVE_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadMoveRequest, action.payload.threadId);
    case 'THREAD.MOVE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadMoveRequest, action.payload.threadId);
    case 'THREAD.MOVE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadMoveRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.REMOVE_PARTICIPANTS_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadRemoveParticipantsRequest, action.payload.messageId);
    case 'THREAD.REMOVE_PARTICIPANTS_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadRemoveParticipantsRequest, action.payload.messageId);
    case 'THREAD.REMOVE_PARTICIPANTS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadRemoveParticipantsRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'THREAD.PROMOTE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PromoteThreadRequest, action.payload.threadId);
    case 'THREAD.PROMOTE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PromoteThreadRequest, action.payload.threadId);
    case 'THREAD.PROMOTE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PromoteThreadRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'THREAD.UNPROMOTE_PENDING':
      return moveRequestToPending(state, RequestStatusType.UnpromoteThreadRequest, action.payload.threadId);
    case 'THREAD.UNPROMOTE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.UnpromoteThreadRequest, action.payload.threadId);
    case 'THREAD.UNPROMOTE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UnpromoteThreadRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'UNSEEN_THREADS.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.UnseenThreadsLoadRequest, action.payload.id);
    case 'UNSEEN_THREADS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.UnseenThreadsLoadRequest, action.payload.id);
    case 'UNSEEN_THREADS.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UnseenThreadsLoadRequest,
        action.payload.id,
        action.payload.error
      );

    case 'USER_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageDeleteRequest, action.payload.userId);
    case 'USER_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageDeleteRequest, action.payload.userId);
    case 'USER_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageDeleteRequest,
        action.payload.userId,
        action.payload.error
      );

    case 'USER_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageUploadRequest, action.payload.userId);
    case 'USER_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageUploadRequest, action.payload.userId);
    case 'USER_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageUploadRequest,
        action.payload.userId,
        action.payload.error
      );

    case 'CAMPAIGN_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageDeleteRequest, action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageDeleteRequest, action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageDeleteRequest,
        action.payload.campaignId,
        action.payload.error
      );

    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.CoverImageUploadRequest, action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CoverImageUploadRequest, action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CoverImageUploadRequest,
        action.payload.campaignId,
        action.payload.error
      );

    case 'FEED_COUNTS.LOAD_GROUP_PENDING':
      return moveRequestToPending(state, RequestStatusType.GroupFeedUnreadCountRequest, action.payload.id);
    case 'FEED_COUNTS.LOAD_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.GroupFeedUnreadCountRequest, action.payload.id);
    case 'FEED_COUNTS.LOAD_GROUP_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupFeedUnreadCountRequest,
        action.payload.id,
        action.payload.error
      );

    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        RequestStatusType.GroupResolveMembershipRequest,
        groupMemberPanelRelatedActions,
        action.payload.groupMembershipRequestId
      );
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.GroupResolveMembershipRequest,
        action.payload.groupMembershipRequestId
      );
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.GroupResolveMembershipRequest,
        action.payload.groupMembershipRequestId,
        action.payload.error
      );

    case 'ACKNOWLEDGE_STREAM_LIVE_EVENT_POLICY.PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.AcknowledgeStreamEventPolicyRequest,
        acknowledgeStreamEventPolicyRequestId
      );
    case 'ACKNOWLEDGE_STREAM_LIVE_EVENT_POLICY.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.AcknowledgeStreamEventPolicyRequest,
        acknowledgeStreamEventPolicyRequestId
      );
    case 'ACKNOWLEDGE_STREAM_LIVE_EVENT_POLICY.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AcknowledgeStreamEventPolicyRequest,
        acknowledgeStreamEventPolicyRequestId,
        action.payload.error
      );

    case 'CANCEL_EVENT.PENDING':
      return moveRequestToPending(state, RequestStatusType.CancelEventRequest, action.payload);
    case 'CANCEL_EVENT.FULFILLED':
      return stateWithCancelledEvent(state, action);
    case 'CANCEL_EVENT.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CancelEventRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'LIVE_EVENT.LOAD_BY_NETWORK_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.LiveEventsLoadByNetworkRequest,
        liveEventsLoadByNetworkRequestId
      );
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.LiveEventsLoadByNetworkRequest,
        liveEventsLoadByNetworkRequestId
      );
    case 'LIVE_EVENT.LOAD_BY_NETWORK_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.LiveEventsLoadByNetworkRequest,
        liveEventsLoadByNetworkRequestId,
        action.payload.error
      );

    case 'EDIT_EVENT.PENDING':
      return moveRequestToPending(state, RequestStatusType.EditEventRequest, action.payload.broadcastId);
    case 'EDIT_EVENT.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.EditEventRequest, action.payload.broadcastId);
    case 'EDIT_EVENT.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditEventRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'EDIT_RESTRICTED_POSTING.PENDING':
      return moveRequestToPending(state, RequestStatusType.EditRestrictedPostingRequest, action.payload.broadcastId);
    case 'EDIT_RESTRICTED_POSTING.FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.EditRestrictedPostingRequest, action.payload.broadcastId);
    case 'EDIT_RESTRICTED_POSTING.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EditRestrictedPostingRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.CreateGroupCalendarInviteForBroadcastRequest,
        action.payload.broadcastId
      );
    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.CreateGroupCalendarInviteForBroadcastRequest,
        action.payload.broadcastId
      );
    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CreateGroupCalendarInviteForBroadcastRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'EVENT.LOAD_BY_BROADCAST_ID_PENDING':
      return moveRequestToPending(state, RequestStatusType.EventLoadByBroadcastIdRequest, action.payload);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.EventLoadByBroadcastIdRequest, action.payload.broadcastId);
    case 'EVENT.LOAD_BY_BROADCAST_ID_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.EventLoadByBroadcastIdRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'COMMUNITY.ADD_FAVORITE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AddFavoriteGroupRequest,
        addFavoriteGroupRequestId,
        action.payload.error
      );

    case 'THREAD.LOAD_NEWER_REPLIES_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadLoadNewerRepliesRequest, action.payload.id);
    case 'THREAD.LOAD_NEWER_REPLIES_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadLoadNewerRepliesRequest, action.payload.id);
    case 'THREAD.LOAD_NEWER_REPLIES_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadLoadNewerRepliesRequest,
        action.payload.id,
        action.payload.error
      );

    case 'THREAD_TOPIC.ADD_PENDING':
      return moveRequestToPending(state, RequestStatusType.AddThreadTopicRequest, action.payload.threadId);
    case 'THREAD_TOPIC.ADD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.AddThreadTopicRequest, action.payload.threadId);
    case 'THREAD_TOPIC.ADD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.AddThreadTopicRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD_TOPIC.REMOVE_PENDING':
      return moveRequestToPending(state, RequestStatusType.RemoveThreadTopicRequest, action.payload.threadId);
    case 'THREAD_TOPIC.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.RemoveThreadTopicRequest, action.payload.threadId);
    case 'THREAD_TOPIC.REMOVE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.RemoveThreadTopicRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNMARK_AS_QUESTION_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadUnmarkAsQuestionRequest,
        removeType: RequestStatusType.ThreadMarkAsQuestionRequest,
        entityId: action.payload.messageId,
      });
    case 'THREAD.UNMARK_AS_QUESTION_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadUnmarkAsQuestionRequest, action.payload.messageId);
    case 'THREAD.UNMARK_AS_QUESTION_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadUnmarkAsQuestionRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'THREAD.MARK_AS_QUESTION_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: RequestStatusType.ThreadMarkAsQuestionRequest,
        removeType: RequestStatusType.ThreadUnmarkAsQuestionRequest,
        entityId: action.payload.messageId,
      });
    case 'THREAD.MARK_AS_QUESTION_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadMarkAsQuestionRequest, action.payload.messageId);
    case 'THREAD.MARK_AS_QUESTION_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadMarkAsQuestionRequest,
        action.payload.messageId,
        action.payload.error
      );

    case 'USER_DELEGATE.REMOVE_PENDING':
      return moveRequestToPending(state, RequestStatusType.DelegationRemoveRequest, removeUserDelegateRequestId);
    case 'USER_DELEGATE.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DelegationRemoveRequest, removeUserDelegateRequestId);
    case 'USER_DELEGATE.REMOVE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DelegationRemoveRequest,
        removeUserDelegateRequestId,
        action.payload.error
      );

    case 'USER_DELEGATE.ADD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DelegationAddRequest,
        addUserDelegateRequestId,
        action.payload.error
      );
    case 'USER_DELEGATE.ADD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DelegationAddRequest, addUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_PENDING':
      return moveRequestToPending(state, RequestStatusType.DelegationEditRequest, editUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DelegationEditRequest, editUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DelegationEditRequest,
        editUserDelegateRequestId,
        action.payload.error
      );

    case 'SEARCH.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.SearchLoadMoreRequest, action.payload.loadMoreRequestId);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.SearchLoadMoreRequest, action.payload.loadMoreRequestId);
    case 'SEARCH.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SearchLoadMoreRequest,
        action.payload.loadMoreRequestId,
        action.payload.error
      );

    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_PENDING':
      return moveRequestToPending(state, RequestStatusType.PinnedResourcesPinLink, action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PinnedResourcesPinLink,
        action.payload.groupId,
        action.payload.error
      );
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_FULFILLED':
      return removeRequest(state, RequestStatusType.PinnedResourcesPinLink, action.payload.groupId);
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignPinnedResourcesPinLink, action.payload.campaignId);
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignPinnedResourcesPinLink,
        action.payload.campaignId,
        action.payload.error
      );
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignPinnedResourcesPinLink, action.payload.campaignId);

    case 'CAMPAIGN.EDIT_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignEditRequest, action.payload.campaignId);
    case 'CAMPAIGN.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignEditRequest,
        action.payload.campaignId,
        action.payload.error
      );
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignEditRequest, action.payload.id);

    case 'CAMPAIGN.DELETE_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignDeleteRequest, action.payload.campaignId);
    case 'CAMPAIGN.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignDeleteRequest,
        action.payload.campaignId,
        action.payload.error
      );
    case 'CAMPAIGN.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignDeleteRequest, action.payload.id);

    case 'CAMPAIGN.FOLLOW_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignFollow, action.payload.id);
    case 'CAMPAIGN.FOLLOW_REJECTED':
      return moveRequestToRejected(state, RequestStatusType.CampaignFollow, action.payload.id, action.payload.error);
    case 'CAMPAIGN.FOLLOW_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignFollow, action.payload.id);

    case 'CAMPAIGN.UNFOLLOW_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignUnfollow, action.payload.id);
    case 'CAMPAIGN.UNFOLLOW_REJECTED':
      return moveRequestToRejected(state, RequestStatusType.CampaignUnfollow, action.payload.id, action.payload.error);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignUnfollow, action.payload.id);

    case 'CAMPAIGN.GOALTRACKER.CREATE_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignGoalTrackerCreateRequest, action.payload.id);
    case 'CAMPAIGN.GOALTRACKER.CREATE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignGoalTrackerCreateRequest,
        action.payload.id,
        action.payload.error
      );
    case 'CAMPAIGN.GOALTRACKER.CREATE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignGoalTrackerCreateRequest, action.payload.id);

    case 'CAMPAIGN.GOALTRACKER.EDIT_PENDING':
      return moveRequestToPending(state, RequestStatusType.CampaignGoalTrackerEditRequest, action.payload.id);
    case 'CAMPAIGN.GOALTRACKER.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignGoalTrackerEditRequest,
        action.payload.id,
        action.payload.error
      );
    case 'CAMPAIGN.GOALTRACKER.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.CampaignGoalTrackerEditRequest, action.payload.id);

    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_PENDING':
      return moveRequestToPending(state, RequestStatusType.PinnedResourcesPinLink, action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.PinnedResourcesPinLink,
        action.payload.groupId,
        action.payload.error
      );
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.PinnedResourcesPinLink, action.payload.groupId);

    case 'INBOX_UNREAD_COUNT.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.InboxUnreadCountRequest, inboxUnreadCountRequestId);
    case 'INBOX_UNREAD_COUNT.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.InboxUnreadCountRequest,
        inboxUnreadCountRequestId,
        action.payload.error
      );
    case 'INBOX_UNREAD_COUNT.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.InboxUnreadCountRequest, inboxUnreadCountRequestId);

    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.TopLevelRepliesLoadInitialRepliesRequest,
        action.payload.threadId
      );
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.TopLevelRepliesLoadInitialRepliesRequest,
        action.payload.threadId
      );
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopLevelRepliesLoadInitialRepliesRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'TOP_LEVEL_REPLIES.LOAD_LAST_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopLevelRepliesLoadNextRequest, action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopLevelRepliesLoadNextRequest, action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopLevelRepliesLoadLastRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopLevelRepliesLoadNextRequest, action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopLevelRepliesLoadNextRequest, action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopLevelRepliesLoadNextRequest,
        action.payload.threadId,
        action.payload.error
      );
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopLevelRepliesLoadPreviousRequest, action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.TopLevelRepliesLoadPreviousRequest,
        action.payload.threadId
      );
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopLevelRepliesLoadPreviousRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'TOPIC.EDIT_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicEditRequest, action.payload.topicId);
    case 'TOPIC.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicEditRequest, action.payload.topicId);
    case 'TOPIC.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicEditRequest,
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'CURATED_TOPIC.REMOVE_PENDING':
      return moveRequestToPending(state, RequestStatusType.RemoveCuratedTopicRequest, action.payload.topicId);
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.RemoveCuratedTopicRequest, action.payload.topicId);
    case 'CURATED_TOPIC.REMOVE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.RemoveCuratedTopicRequest,
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'TOPIC.CURATE_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicCurateRequest, action.payload.topicId);
    case 'TOPIC.CURATE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicCurateRequest, action.payload.topicId);
    case 'TOPIC.CURATE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicCurateRequest,
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'TOPICS_CURATED.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicsCuratedLoadRequest, action.topicsFilter);
    case 'TOPICS_CURATED.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicsCuratedLoadRequest, action.payload.topicsFilter);
    case 'TOPICS_CURATED.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicsCuratedLoadRequest,
        action.payload.topicsFilter,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'TOPICS_CURATED.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicsCuratedLoadMoreRequest, action.topicsFilter);
    case 'TOPICS_CURATED.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicsCuratedLoadMoreRequest, action.payload.topicsFilter);
    case 'TOPICS_CURATED.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicsCuratedLoadMoreRequest,
        action.payload.topicsFilter,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'TOPIC.UNCURATE_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicUncurateRequest, action.payload.topicId);
    case 'TOPIC.UNCURATE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicUncurateRequest, action.payload.topicId);
    case 'TOPIC.UNCURATE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicUncurateRequest,
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'TOPIC_SIDEBAR.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.TopicSidebarLoadRequest, action.payload.topicId);
    case 'TOPIC_SIDEBAR.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.TopicSidebarLoadRequest, action.payload.topicId);
    case 'TOPIC_SIDEBAR.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicSidebarLoadRequest,
        action.payload.topicId,
        action.payload.error
      );

    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.SecondLevelRepliesLoadNextRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.SecondLevelRepliesLoadNextRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SecondLevelRepliesLoadNextRequest,
        action.payload.topLevelMessageId,
        action.payload.error
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.SecondLevelRepliesLoadPreviousRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.SecondLevelRepliesLoadPreviousRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SecondLevelRepliesLoadPreviousRequest,
        action.payload.topLevelMessageId,
        action.payload.error
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.SecondLevelRepliesLoadLastRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.SecondLevelRepliesLoadLastRequest,
        action.payload.topLevelMessageId
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SecondLevelRepliesLoadLastRequest,
        action.payload.topLevelMessageId,
        action.payload.error
      );

    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.ReactionAccentColorUpdateRequest,
        updateReactionAccentColorRequestId
      );
    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.ReactionAccentColorUpdateRequest,
        updateReactionAccentColorRequestId
      );
    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ReactionAccentColorUpdateRequest,
        updateReactionAccentColorRequestId,
        action.payload.error
      );

    case 'SHAREPOINT_VIDEO_LINK.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.SharepointVideoLinkLoadRequest, action.payload.id);
    case 'SHAREPOINT_VIDEO_LINK.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.SharepointVideoLinkLoadRequest, action.payload.id);
    case 'SHAREPOINT_VIDEO_LINK.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SharepointVideoLinkLoadRequest,
        action.payload.id,
        action.payload.error
      );

    case 'WEBLINK.RESOLVE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.WebLinkResolveRequest, action.payload.link);
    case 'WEBLINK.RESOLVE_PENDING':
      return moveRequestToPending(state, RequestStatusType.WebLinkResolveRequest, action.payload.link);
    case 'WEBLINK.RESOLVE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.WebLinkResolveRequest,
        action.payload.link,
        action.payload.error
      );

    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_PENDING':
      return moveRequestToPending(state, RequestStatusType.DownloadIcsRequest, action.payload.broadcastId);
    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.DownloadIcsRequest, action.payload.broadcastId);
    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DownloadIcsRequest,
        action.payload.broadcastId,
        action.payload.error
      );

    case 'THREAD_LIST.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadListLoadRequest, threadListLoadRequestId);
    case 'THREAD_LIST.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadListLoadRequest, threadListLoadRequestId);
    case 'THREAD_LIST.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadListLoadRequest,
        threadListLoadRequestId,
        action.payload.error
      );

    case 'NESTED_THREAD_LIST.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadListLoadRequest, threadListLoadRequestId);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadListLoadRequest, threadListLoadRequestId);
    case 'NESTED_THREAD_LIST.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadListLoadRequest,
        threadListLoadRequestId,
        action.payload.error
      );

    case 'THREAD_SHARES.LOAD_PENDING':
      return moveRequestToPending(state, RequestStatusType.ThreadShareLoadRequest, action.payload.threadId);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.ThreadShareLoadRequest, action.payload.threadId);
    case 'THREAD_SHARES.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.ThreadShareLoadRequest,
        action.payload.threadId,
        action.payload.error
      );

    case 'MY_GROUPS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.MyGroupsLoadMoreRequest, `${action.payload.filteredBy}`);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return removeRequest(state, RequestStatusType.MyGroupsLoadMoreRequest, `${action.payload.filteredBy}`);
    case 'MY_GROUPS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.MyGroupsLoadMoreRequest,
        `${action.payload.filteredBy}`,
        action.payload.error
      );

    case 'TOPICS_FOLLOWED.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.TopicsFollowedLoadMoreRequest,
        `${action.topicsFollowedRequestStatusKey}`
      );
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.TopicsFollowedLoadMoreRequest,
        `${action.topicsFollowedRequestStatusKey}`
      );
    case 'TOPICS_FOLLOWED.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TopicsFollowedLoadMoreRequest,
        `${action.topicsFollowedRequestStatusKey}`,
        action.payload.error
      );

    case 'TRENDING_TOPICS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.TrendingTopicsLoadMoreRequest,
        `${action.trendingTopicsRequestStatusKey}`
      );
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.TrendingTopicsLoadMoreRequest,
        `${action.trendingTopicsRequestStatusKey}`
      );
    case 'TRENDING_TOPICS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.TrendingTopicsLoadMoreRequest,
        `${action.trendingTopicsRequestStatusKey}`,
        action.payload.error
      );

    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.UserNetworkLeadersListMoreRequest,
        `${action.userNetworkLeadersListRequestStatusKey}`
      );
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.UserNetworkLeadersListMoreRequest,
        `${action.userNetworkLeadersListRequestStatusKey}`
      );
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UserNetworkLeadersListMoreRequest,
        `${action.userNetworkLeadersListRequestStatusKey}`,
        action.payload.error
      );
    case 'ALL_CAMPAIGNS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.CampaignsListMoreRequest,
        `${action.campaignsListRequestStatusKey}`
      );
    case 'ALL_CAMPAIGNS.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.CampaignsListMoreRequest,
        `${action.campaignsListRequestStatusKey}`
      );
    case 'ALL_CAMPAIGNS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CampaignsListMoreRequest,
        `${action.campaignsListRequestStatusKey}`,
        action.payload.error
      );
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.PENDING':
      return moveRequestToPending(state, RequestStatusType.UpdateHasNetworkWideAudienceRequest, action.payload.userId);
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.UpdateHasNetworkWideAudienceRequest,
        action.payload.userId
      );
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UpdateHasNetworkWideAudienceRequest,
        action.payload.userId,
        action.payload.error
      );

    case 'USER_DIRECT_AUDIENCE.LOAD_MORE_PENDING':
      return moveRequestToPending(state, RequestStatusType.UserDirectAudienceLoadMoreRequest, action.payload.userId);
    case 'USER_DIRECT_AUDIENCE.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.UserDirectAudienceLoadMoreRequest, action.payload.userId);
    case 'USER_DIRECT_AUDIENCE.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UserDirectAudienceLoadMoreRequest,
        action.payload.userId,
        action.payload.error
      );

    case 'CONNECTED_LEADERS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.UserConnectedLeadersListLoadMoreRequest,
        action.userConnectedLeadersListRequestStatusKey
      );
    case 'CONNECTED_LEADERS.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.UserConnectedLeadersListLoadMoreRequest,
        action.userConnectedLeadersListRequestStatusKey
      );
    case 'CONNECTED_LEADERS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UserConnectedLeadersListLoadMoreRequest,
        action.userConnectedLeadersListRequestStatusKey,
        action.payload.error
      );

    case 'FOLLOWED_LEADERS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.UserFollowedLeadersListLoadMoreRequest,
        action.userFollowedLeadersListRequestStatusKey
      );
    case 'FOLLOWED_LEADERS.LOAD_MORE_FULFILLED':
      return removeRequest(
        state,
        RequestStatusType.UserFollowedLeadersListLoadMoreRequest,
        action.userFollowedLeadersListRequestStatusKey
      );
    case 'FOLLOWED_LEADERS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UserFollowedLeadersListLoadMoreRequest,
        action.userFollowedLeadersListRequestStatusKey,
        action.payload.error
      );

    case 'ADMIN_ROLES.UPDATE_ROLES_PENDING':
      return moveRequestToPending(state, RequestStatusType.UpdateUserAdminRolesRequest, action.payload.userId);
    case 'ADMIN_ROLES.UPDATE_ROLES_FULFILLED':
      return moveRequestToFulfilled(state, RequestStatusType.UpdateUserAdminRolesRequest, action.payload.userId);
    case 'ADMIN_ROLES.UPDATE_ROLES_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UpdateUserAdminRolesRequest,
        action.payload.userId,
        action.payload.error
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.UpdateStorylineDefaultNotificationDeliverySettingsRequest,
        updateStorylineDefaultNotificationDeliverySettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.UpdateStorylineDefaultNotificationDeliverySettingsRequest,
        updateStorylineDefaultNotificationDeliverySettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.UpdateStorylineDefaultNotificationDeliverySettingsRequest,
        updateStorylineDefaultNotificationDeliverySettingsRequestId,
        action.payload.error
      );

    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.DesignateLeadersFromOfficeGroupAsyncActionRequest,
        action.payload.officeGroupId
      );
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.DesignateLeadersFromOfficeGroupAsyncActionRequest,
        action.payload.officeGroupId
      );
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.DesignateLeadersFromOfficeGroupAsyncActionRequest,
        action.payload.officeGroupId,
        action.payload.error
      );

    case 'SUGGESTED_TOPICS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.SuggestedTopicsLoadMoreRequest,
        suggestedTopicsLoadMoreRequestId
      );
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return removeRequest(state, RequestStatusType.SuggestedTopicsLoadMoreRequest, suggestedTopicsLoadMoreRequestId);
    case 'SUGGESTED_TOPICS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.SuggestedTopicsLoadMoreRequest,
        suggestedTopicsLoadMoreRequestId,
        action.payload.error
      );

    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.PENDING':
      return moveRequestToPending(
        state,
        RequestStatusType.CreateCalendarInviteForTeamsMeetingRequest,
        action.payload.teamsMeetingId
      );
    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(
        state,
        RequestStatusType.CreateCalendarInviteForTeamsMeetingRequest,
        action.payload.teamsMeetingId
      );
    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(
        state,
        RequestStatusType.CreateCalendarInviteForTeamsMeetingRequest,
        action.payload.teamsMeetingId,
        action.payload.error
      );

    default:
      return state;
  }
};

export default reducer;
